import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import Swal from "sweetalert2";

import {
    FaBars,
    FaBookOpen,
    FaCalendarCheck,
    FaCheck,
    FaChevronDown,
    FaChevronLeft,
    FaChevronRight,
    FaChevronUp,
    FaClock,
    FaGraduationCap,
    FaLock,
    FaPlay,
    FaPlayCircle,
    FaSearch,
    FaTimes,
    FaTrash,
    FaUserTie,
} from "react-icons/fa";

import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";
import ClassRoomEnvironment from "../../ViewPages/ClassRoomEnvironment";

const SUPPORT_PHONE =
    process.env.REACT_APP_COURSE_SUPPORT_PHONE ||
    "YOUR FINANCE OFFICE NUMBER";

const SUPPORT_EMAIL =
    process.env.REACT_APP_COURSE_SUPPORT_EMAIL ||
    "YOUR FINANCE OFFICE EMAIL";

const COURSE_PAGE_SIZE = 12;
const INITIAL_VISIBLE_SECTION_COUNT = 4;

function MyLearningCoursesEnvironment() {
    const {
        selectedYear,
        initialized: academicYearInitialized,
        loading: academicYearLoading,
    } = useAcademicYear();

    const [courses, setCourses] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState(null);

    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [dropping, setDropping] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [courseSections, setCourseSections] = useState({});
    const [sectionsLoading, setSectionsLoading] = useState({});
    const [sectionsErrors, setSectionsErrors] = useState({});

    const [expandedCourses, setExpandedCourses] = useState({});

    const [activeVideo, setActiveVideo] = useState(null);
    const [completedVideos, setCompletedVideos] = useState({});

    const [videoError, setVideoError] = useState("");
    const [videoLoading, setVideoLoading] = useState(false);

    const [pendingAutoAdvance, setPendingAutoAdvance] =
        useState(false);

    const videoRef = useRef(null);

    const swalCayep = useMemo(
        () =>
            Swal.mixin({
                background: "#FFFFFF",
                color: "#142536",
                confirmButtonColor: "#062A4B",
                cancelButtonColor: "#C0750D",
                customClass: {
                    popup: "cayep-swal-popup",
                    title: "cayep-swal-title",
                    htmlContainer: "cayep-swal-content",
                },
            }),
        []
    );

    const getErrorMessage = useCallback(
        (error, fallback) => {
            const data = error?.response?.data;

            if (!data) {
                return error?.message || fallback;
            }

            if (typeof data === "string") {
                return data;
            }

            if (typeof data.detail === "string") {
                return data.detail;
            }

            if (typeof data.message === "string") {
                return data.message;
            }

            if (typeof data.error === "string") {
                return data.error;
            }

            if (typeof data.code === "string") {
                if (data.code === "PAYMENT_REQUIRED") {
                    return "Your payment record for the active academic year could not be verified.";
                }

                return data.code;
            }

            if (typeof data === "object") {
                const messages = [];

                Object.entries(data).forEach(
                    ([field, value]) => {
                        if (Array.isArray(value)) {
                            value.forEach((item) => {
                                if (item !== null && item !== undefined) {
                                    messages.push(
                                        `${field}: ${String(item)}`
                                    );
                                }
                            });
                            return;
                        }

                        if (
                            value &&
                            typeof value === "object"
                        ) {
                            messages.push(
                                `${field}: ${JSON.stringify(value)}`
                            );
                            return;
                        }

                        if (
                            value !== null &&
                            value !== undefined
                        ) {
                            messages.push(
                                `${field}: ${String(value)}`
                            );
                        }
                    }
                );

                if (messages.length) {
                    return messages.join("\n");
                }
            }

            return fallback;
        },
        []
    );

    const isApprovedCourse = useCallback(
        (course) =>
            String(
                course?.learning_status || ""
            ).toLowerCase() === "approved",
        []
    );

    const isPendingCourse = useCallback(
        (course) => {
            const status = String(
                course?.learning_status || "pending"
            ).toLowerCase();

            return status !== "approved";
        },
        []
    );

    const formatApprovalDate = useCallback(
        (approvedAt) => {
            if (!approvedAt) {
                return "";
            }

            const date = new Date(approvedAt);

            if (Number.isNaN(date.getTime())) {
                return "";
            }

            return new Intl.DateTimeFormat(
                undefined,
                {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                }
            ).format(date);
        },
        []
    );

    const getStatusLabel = useCallback(
        (course) => {
            if (isApprovedCourse(course)) {
                return "Approved";
            }

            return "Pending / Verifying";
        },
        [isApprovedCourse]
    );

    const getStatusClass = useCallback(
        (course) => {
            if (isApprovedCourse(course)) {
                return "approved";
            }

            return "pending";
        },
        [isApprovedCourse]
    );

    const getApiOrigin = useCallback(() => {
        try {
            const configuredBaseUrl =
                api?.defaults?.baseURL;

            if (configuredBaseUrl) {
                return new URL(
                    configuredBaseUrl,
                    window.location.origin
                ).origin;
            }
        } catch (error) {
            return window.location.origin;
        }

        return window.location.origin;
    }, []);

    const getMediaUrl = useCallback(
        (value) => {
            if (!value) {
                return "";
            }

            const stringValue = String(value).trim();

            if (!stringValue) {
                return "";
            }

            if (
                /^(https?:|blob:|data:)/i.test(
                    stringValue
                )
            ) {
                return stringValue;
            }

            const origin = getApiOrigin();

            if (stringValue.startsWith("/")) {
                return `${origin}${stringValue}`;
            }

            return `${origin}/${stringValue.replace(
                /^\/+/,
                ""
            )}`;
        },
        [getApiOrigin]
    );

    const getCourseVideoUrl = useCallback(
        (course) =>
            getMediaUrl(
                course?.video_url ||
                    course?.video ||
                    course?.course_video ||
                    course?.video_file
            ),
        [getMediaUrl]
    );

    const getCourseThumbnailUrl = useCallback(
        (course) =>
            getMediaUrl(
                course?.thumbnail ||
                    course?.thumbnail_url ||
                    course?.image ||
                    course?.course_image
            ),
        [getMediaUrl]
    );

    const getSectionVideoUrl = useCallback(
        (section) =>
            getMediaUrl(
                section?.video_url ||
                    section?.video ||
                    section?.video_file ||
                    section?.file
            ),
        [getMediaUrl]
    );

    const getSectionNumber = useCallback(
        (section) =>
            Number(
                section?.section_number ??
                    section?.number ??
                    section?.section ??
                    0
            ),
        []
    );

    const getSectionDuration = useCallback(
        (section) =>
            Number(
                section?.duration_seconds ??
                    section?.video_duration_seconds ??
                    0
            ),
        []
    );

    const formatDuration = useCallback(
        (seconds) => {
            const value = Number(seconds);

            if (
                !Number.isFinite(value) ||
                value <= 0
            ) {
                return "";
            }

            const totalSeconds = Math.round(value);
            const hours = Math.floor(
                totalSeconds / 3600
            );
            const minutes = Math.floor(
                (totalSeconds % 3600) / 60
            );
            const remainingSeconds =
                totalSeconds % 60;

            if (hours > 0) {
                if (minutes === 0) {
                    return `${hours}h`;
                }

                return `${hours}h ${minutes}m`;
            }

            if (minutes > 0) {
                if (remainingSeconds === 0) {
                    return `${minutes}m`;
                }

                return `${minutes}m ${remainingSeconds}s`;
            }

            return `${remainingSeconds}s`;
        },
        []
    );

    const formatPlayerDuration = useCallback(
        (seconds) => {
            const value = Number(seconds);

            if (
                !Number.isFinite(value) ||
                value <= 0
            ) {
                return "—";
            }

            const total = Math.round(value);

            const hours = Math.floor(
                total / 3600
            );

            const minutes = Math.floor(
                (total % 3600) / 60
            );

            const secs = total % 60;

            if (hours > 0) {
                return `${String(hours).padStart(
                    2,
                    "0"
                )}:${String(minutes).padStart(
                    2,
                    "0"
                )}:${String(secs).padStart(
                    2,
                    "0"
                )}`;
            }

            return `${String(minutes).padStart(
                2,
                "0"
            )}:${String(secs).padStart(
                2,
                "0"
            )}`;
        },
        []
    );

    const showPendingNotice = useCallback(
        async (course) => {
            await swalCayep.fire({
                icon: "warning",
                title: "Course Access Pending",
                html: `
                    <div class="cayep-pending-alert">
                        <div class="cayep-pending-alert-icon">
                            <span>☎</span>
                        </div>

                        <div class="cayep-pending-alert-title">
                            Payment Verification Required
                        </div>

                        <p>
                            Your payment has not yet been
                            processed or approved for
                            <strong>
                                ${course?.course_title || "this course"}
                            </strong>.
                        </p>

                        <p>
                            Please contact the Customer Support
                            or Finance Office for details.
                        </p>

                        <div class="cayep-pending-contact">
                            <div>
                                <span class="cayep-contact-icon">
                                    ☎
                                </span>
                                <strong>
                                    Support / Finance:
                                </strong>
                                <span>
                                    ${SUPPORT_PHONE}
                                </span>
                            </div>

                            <div>
                                <span class="cayep-contact-icon">
                                    ✉
                                </span>
                                <strong>
                                    Email:
                                </strong>
                                <span>
                                    ${SUPPORT_EMAIL}
                                </span>
                            </div>
                        </div>

                        <div class="cayep-pending-note">
                            Your course will become available
                            automatically here after an authorized
                            administrator approves your payment.
                        </div>
                    </div>
                `,
                confirmButtonText: "Understood",
            });
        },
        [swalCayep]
    );

    const fetchCourses = useCallback(
        async () => {
            if (
                !academicYearInitialized ||
                !selectedYear
            ) {
                return;
            }

            setLoading(true);
            setErrorMessage("");

            try {
                const params = {
                    page,
                    page_size: COURSE_PAGE_SIZE,
                };

                if (search.trim()) {
                    params.search = search.trim();
                }

                const response = await api.get(
                    "/api/course-learning/learning/",
                    {
                        params,
                    }
                );

                const data = response.data || {};

                const results = Array.isArray(
                    data.results
                )
                    ? data.results
                    : Array.isArray(data)
                    ? data
                    : [];

                setCourses(results);

                const count = Number(
                    data.count ?? results.length
                );

                setTotalPages(
                    Math.max(
                        1,
                        Math.ceil(
                            count / COURSE_PAGE_SIZE
                        )
                    )
                );

                setSelectedCourse((current) => {
                    if (!results.length) {
                        return null;
                    }

                    if (!current) {
                        const firstApproved =
                            results.find(
                                (item) =>
                                    isApprovedCourse(
                                        item
                                    )
                            );

                        return (
                            firstApproved ||
                            results[0]
                        );
                    }

                    return (
                        results.find(
                            (item) =>
                                item.id ===
                                current.id
                        ) || null
                    );
                });
            } catch (error) {
                const code =
                    error?.response?.data?.code;

                if (
                    code ===
                    "PAYMENT_REQUIRED"
                ) {
                    setCourses([]);
                    setSelectedCourse(null);

                    setErrorMessage(
                        "Your payment record for the active academic year could not be verified."
                    );

                    return;
                }

                setErrorMessage(
                    getErrorMessage(
                        error,
                        "Unable to load your learning environment."
                    )
                );
            } finally {
                setLoading(false);
            }
        },
        [
            academicYearInitialized,
            selectedYear,
            page,
            search,
            getErrorMessage,
            isApprovedCourse,
        ]
    );

    useEffect(() => {
        const timer = window.setTimeout(
            () => {
                fetchCourses();
            },
            250
        );

        return () =>
            window.clearTimeout(timer);
    }, [fetchCourses]);

    const getProgressStorageKey =
        useCallback((courseId) => {
            return `cayep-learning-video-progress-${courseId}`;
        }, []);

    const loadCompletedVideos = useCallback(
        (courseId) => {
            if (!courseId) {
                return new Set();
            }

            try {
                const stored =
                    localStorage.getItem(
                        getProgressStorageKey(
                            courseId
                        )
                    );

                if (!stored) {
                    return new Set();
                }

                const parsed =
                    JSON.parse(stored);

                if (!Array.isArray(parsed)) {
                    return new Set();
                }

                return new Set(
                    parsed.map((item) =>
                        String(item)
                    )
                );
            } catch (error) {
                return new Set();
            }
        },
        [getProgressStorageKey]
    );

    const saveCompletedVideos =
        useCallback(
            (courseId, completedSet) => {
                if (!courseId) {
                    return;
                }

                try {
                    localStorage.setItem(
                        getProgressStorageKey(
                            courseId
                        ),
                        JSON.stringify(
                            Array.from(
                                completedSet
                            )
                        )
                    );
                } catch (error) {
                    return;
                }
            },
            [getProgressStorageKey]
        );

    const setCourseCompletedState =
        useCallback(
            (courseId, completedSet) => {
                setCompletedVideos(
                    (current) => ({
                        ...current,
                        [courseId]:
                            new Set(
                                completedSet
                            ),
                    })
                );
            },
            []
        );

    const loadCourseSections =
        useCallback(
            async (courseId) => {
                if (!courseId) {
                    return [];
                }

                setSectionsLoading(
                    (current) => ({
                        ...current,
                        [courseId]: true,
                    })
                );

                setSectionsErrors(
                    (current) => ({
                        ...current,
                        [courseId]: "",
                    })
                );

                try {
                    const response =
                        await api.get(
                            `/api/users/subjects/${courseId}/video-sections/`
                        );

                    const data =
                        response.data || {};

                    let sections = [];

                    if (
                        Array.isArray(data)
                    ) {
                        sections = data;
                    } else if (
                        Array.isArray(
                            data.results
                        )
                    ) {
                        sections =
                            data.results;
                    } else if (
                        Array.isArray(
                            data.sections
                        )
                    ) {
                        sections =
                            data.sections;
                    } else if (
                        Array.isArray(
                            data.video_sections
                        )
                    ) {
                        sections =
                            data.video_sections;
                    }

                    sections =
                        sections
                            .filter(Boolean)
                            .sort(
                                (a, b) =>
                                    getSectionNumber(
                                        a
                                    ) -
                                        getSectionNumber(
                                            b
                                        ) ||
                                    Number(
                                        a.id
                                    ) -
                                        Number(
                                            b.id
                                        )
                            );

                    setCourseSections(
                        (current) => ({
                            ...current,
                            [courseId]:
                                sections,
                        })
                    );

                    return sections;
                } catch (error) {
                    const message =
                        getErrorMessage(
                            error,
                            "Unable to load the video sections for this course."
                        );

                    setSectionsErrors(
                        (current) => ({
                            ...current,
                            [courseId]:
                                message,
                        })
                    );

                    setCourseSections(
                        (current) => ({
                            ...current,
                            [courseId]: [],
                        })
                    );

                    return [];
                } finally {
                    setSectionsLoading(
                        (current) => ({
                            ...current,
                            [courseId]: false,
                        })
                    );
                }
            },
            [
                getErrorMessage,
                getSectionNumber,
            ]
        );

    useEffect(() => {
        if (!selectedCourse?.id) {
            return;
        }

        const courseId =
            selectedCourse.id;

        const existing =
            courseSections[courseId];

        if (
            existing !== undefined ||
            sectionsLoading[courseId]
        ) {
            return;
        }

        loadCourseSections(courseId);
    }, [
        selectedCourse,
        courseSections,
        sectionsLoading,
        loadCourseSections,
    ]);

    const buildCurriculum =
        useCallback(
            (course) => {
                if (!course) {
                    return [];
                }

                const courseId =
                    course.id;

                const introductionUrl =
                    getCourseVideoUrl(
                        course
                    );

                const introduction = {
                    key: `course-${courseId}-introduction`,
                    courseId,
                    type: "introduction",
                    id: `course-${courseId}-introduction`,
                    sectionId: null,
                    sectionNumber: 1,
                    index: 0,
                    title:
                        course?.video_title ||
                        "Course Introduction",
                    description:
                        course?.video_description ||
                        course?.description ||
                        "Introduction to this course.",
                    videoUrl:
                        introductionUrl,
                    thumbnail:
                        getCourseThumbnailUrl(
                            course
                        ),
                    durationSeconds:
                        Number(
                            course?.video_duration_seconds ??
                                0
                        ),
                };

                const sections =
                    courseSections[
                        courseId
                    ] || [];

                const sectionVideos =
                    sections.map(
                        (
                            section,
                            sectionIndex
                        ) => ({
                            key: `course-${courseId}-section-${section.id}`,
                            courseId,
                            type: "section",
                            id: section.id,
                            sectionId:
                                section.id,
                            sectionNumber:
                                getSectionNumber(
                                    section
                                ) ||
                                sectionIndex +
                                    2,
                            index:
                                sectionIndex +
                                1,
                            title:
                                section?.title ||
                                `Video ${
                                    sectionIndex +
                                    2
                                }`,
                            description:
                                section?.description ||
                                "",
                            videoUrl:
                                getSectionVideoUrl(
                                    section
                                ),
                            thumbnail:
                                getCourseThumbnailUrl(
                                    course
                                ),
                            durationSeconds:
                                getSectionDuration(
                                    section
                                ),
                            raw:
                                section,
                        })
                    );

                return [
                    introduction,
                    ...sectionVideos,
                ];
            },
            [
                courseSections,
                getCourseVideoUrl,
                getCourseThumbnailUrl,
                getSectionVideoUrl,
                getSectionNumber,
                getSectionDuration,
            ]
        );

    const currentCourseCurriculum =
        useMemo(
            () =>
                buildCurriculum(
                    selectedCourse
                ),
            [
                buildCurriculum,
                selectedCourse,
            ]
        );

    const selectedCourseCompleted =
        useMemo(() => {
            if (!selectedCourse?.id) {
                return new Set();
            }

            return (
                completedVideos[
                    selectedCourse.id
                ] || new Set()
            );
        }, [
            selectedCourse,
            completedVideos,
        ]);

    const isVideoCompleted =
        useCallback(
            (courseId, videoKey) => {
                if (
                    !courseId ||
                    !videoKey
                ) {
                    return false;
                }

                const completed =
                    completedVideos[
                        courseId
                    ];

                return Boolean(
                    completed?.has(
                        videoKey
                    )
                );
            },
            [completedVideos]
        );

    const isCurriculumVideoUnlocked =
        useCallback(
            (
                curriculum,
                index,
                completedSet
            ) => {
                if (
                    !Array.isArray(
                        curriculum
                    )
                ) {
                    return false;
                }

                if (index <= 0) {
                    return true;
                }

                const previous =
                    curriculum[
                        index - 1
                    ];

                if (!previous) {
                    return false;
                }

                return completedSet.has(
                    previous.key
                );
            },
            []
        );

    const activeCurriculumVideo =
        useMemo(() => {
            if (!selectedCourse?.id) {
                return null;
            }

            if (!activeVideo) {
                return (
                    currentCourseCurriculum[0] ||
                    null
                );
            }

            const found =
                currentCourseCurriculum.find(
                    (item) =>
                        item.key ===
                        activeVideo.key
                );

            return (
                found ||
                currentCourseCurriculum[0] ||
                null
            );
        }, [
            selectedCourse,
            activeVideo,
            currentCourseCurriculum,
        ]);

    useEffect(() => {
        if (!selectedCourse?.id) {
            setActiveVideo(null);
            setVideoError("");
            return;
        }

        const completed =
            loadCompletedVideos(
                selectedCourse.id
            );

        setCourseCompletedState(
            selectedCourse.id,
            completed
        );

        const curriculum =
            buildCurriculum(
                selectedCourse
            );

        if (!curriculum.length) {
            setActiveVideo(null);
            return;
        }

        setActiveVideo((current) => {
            if (!current) {
                return curriculum[0];
            }

            const found =
                curriculum.find(
                    (item) =>
                        item.key ===
                        current.key
                );

            if (
                found &&
                isCurriculumVideoUnlocked(
                    curriculum,
                    found.index,
                    completed
                )
            ) {
                return found;
            }

            const lastUnlocked =
                curriculum.find(
                    (item, index) =>
                        !completed.has(
                            item.key
                        ) &&
                        isCurriculumVideoUnlocked(
                            curriculum,
                            index,
                            completed
                        )
                );

            return (
                lastUnlocked ||
                curriculum[0]
            );
        });
    }, [
        selectedCourse,
        loadCompletedVideos,
        setCourseCompletedState,
        buildCurriculum,
        isCurriculumVideoUnlocked,
    ]);

    const selectCurriculumVideo =
        useCallback(
            async (video) => {
                if (!video || !selectedCourse) {
                    return;
                }

                const curriculum =
                    buildCurriculum(
                        selectedCourse
                    );

                const completed =
                    completedVideos[
                        selectedCourse.id
                    ] ||
                    new Set();

                const targetIndex =
                    curriculum.findIndex(
                        (item) =>
                            item.key ===
                            video.key
                    );

                if (
                    targetIndex < 0
                ) {
                    return;
                }

                const unlocked =
                    isCurriculumVideoUnlocked(
                        curriculum,
                        targetIndex,
                        completed
                    );

                if (!unlocked) {
                    await swalCayep.fire({
                        icon: "info",
                        title: "Lesson Locked",
                        text:
                            "Complete the previous video before continuing to this lesson.",
                        confirmButtonText:
                            "Continue Learning",
                    });

                    return;
                }

                if (!video.videoUrl) {
                    await swalCayep.fire({
                        icon: "warning",
                        title: "Video Not Available",
                        text:
                            "This lesson does not have a playable video yet.",
                        confirmButtonText:
                            "Understood",
                    });

                    return;
                }

                setVideoError("");
                setVideoLoading(true);
                setActiveVideo(video);
                setSidebarOpen(false);

                window.setTimeout(() => {
                    const element =
                        document.querySelector(
                            ".cayep-learning-video-player"
                        );

                    if (
                        element &&
                        window.innerWidth <
                            801
                    ) {
                        element.scrollIntoView(
                            {
                                behavior:
                                    "smooth",
                                block:
                                    "start",
                            }
                        );
                    }
                }, 120);
            },
            [
                selectedCourse,
                buildCurriculum,
                completedVideos,
                isCurriculumVideoUnlocked,
                swalCayep,
            ]
        );

    const markVideoCompleted =
        useCallback(
            (video) => {
                if (
                    !video ||
                    !selectedCourse?.id
                ) {
                    return;
                }

                const courseId =
                    selectedCourse.id;

                const existing =
                    completedVideos[
                        courseId
                    ] ||
                    loadCompletedVideos(
                        courseId
                    );

                const next =
                    new Set(existing);

                next.add(video.key);

                saveCompletedVideos(
                    courseId,
                    next
                );

                setCourseCompletedState(
                    courseId,
                    next
                );
            },
            [
                selectedCourse,
                completedVideos,
                loadCompletedVideos,
                saveCompletedVideos,
                setCourseCompletedState,
            ]
        );

    const handleVideoCompleted =
        useCallback(() => {
            if (
                !activeCurriculumVideo ||
                !selectedCourse?.id
            ) {
                return;
            }

            markVideoCompleted(
                activeCurriculumVideo
            );

            const curriculum =
                buildCurriculum(
                    selectedCourse
                );

            const currentIndex =
                curriculum.findIndex(
                    (item) =>
                        item.key ===
                        activeCurriculumVideo.key
                );

            const nextVideo =
                currentIndex >= 0
                    ? curriculum[
                          currentIndex + 1
                      ]
                    : null;

            if (
                !nextVideo
            ) {
                setPendingAutoAdvance(
                    false
                );

                return;
            }

            setPendingAutoAdvance(
                true
            );

            window.setTimeout(
                () => {
                    const completed =
                        new Set(
                            completedVideos[
                                selectedCourse.id
                            ] ||
                                loadCompletedVideos(
                                    selectedCourse.id
                                )
                        );

                    completed.add(
                        activeCurriculumVideo.key
                    );

                    if (
                        isCurriculumVideoUnlocked(
                            curriculum,
                            nextVideo.index,
                            completed
                        )
                    ) {
                        setActiveVideo(
                            nextVideo
                        );

                        setVideoError(
                            ""
                        );

                        window.setTimeout(
                            () => {
                                const player =
                                    videoRef.current;

                                if (
                                    player
                                ) {
                                    try {
                                        player
                                            .play()
                                            .catch(
                                                () => {}
                                            );
                                    } catch (
                                        error
                                    ) {
                                        return;
                                    }
                                }
                            },
                            250
                        );
                    }

                    setPendingAutoAdvance(
                        false
                    );
                },
                350
            );
        }, [
            activeCurriculumVideo,
            selectedCourse,
            markVideoCompleted,
            buildCurriculum,
            completedVideos,
            loadCompletedVideos,
            isCurriculumVideoUnlocked,
        ]);

    useEffect(() => {
        const player =
            videoRef.current;

        if (!player) {
            return;
        }

        try {
            player.pause();
            player.currentTime = 0;
        } catch (error) {
            return;
        }
    }, [
        activeCurriculumVideo?.key,
    ]);

    const toggleCourseExpansion =
        useCallback(
            (courseId) => {
                setExpandedCourses(
                    (current) => ({
                        ...current,
                        [courseId]:
                            !current[
                                courseId
                            ],
                    })
                );
            },
            []
        );

    const getCourseCurriculum =
        useCallback(
            (course) =>
                buildCurriculum(
                    course
                ),
            [buildCurriculum]
        );

    const getCourseCompletedSet =
        useCallback(
            (courseId) => {
                return (
                    completedVideos[
                        courseId
                    ] ||
                    loadCompletedVideos(
                        courseId
                    )
                );
            },
            [
                completedVideos,
                loadCompletedVideos,
            ]
        );

    const getCourseVisibleSections =
        useCallback(
            (course) => {
                const curriculum =
                    getCourseCurriculum(
                        course
                    );

                const sections =
                    curriculum.slice(
                        1
                    );

                const expanded =
                    Boolean(
                        expandedCourses[
                            course.id
                        ]
                    );

                if (expanded) {
                    return sections;
                }

                return sections.slice(
                    0,
                    INITIAL_VISIBLE_SECTION_COUNT
                );
            },
            [
                getCourseCurriculum,
                expandedCourses,
            ]
        );

    const selectCourse =
        useCallback(
            async (course) => {
                if (
                    isPendingCourse(
                        course
                    )
                ) {
                    await showPendingNotice(
                        course
                    );
                    return;
                }

                setSelectedCourse(
                    course
                );
                setSidebarOpen(false);
                setVideoError("");

                const completed =
                    loadCompletedVideos(
                        course.id
                    );

                setCourseCompletedState(
                    course.id,
                    completed
                );

                const existingSections =
                    courseSections[
                        course.id
                    ];

                if (
                    existingSections ===
                    undefined
                ) {
                    await loadCourseSections(
                        course.id
                    );
                }
            },
            [
                isPendingCourse,
                showPendingNotice,
                loadCompletedVideos,
                setCourseCompletedState,
                courseSections,
                loadCourseSections,
            ]
        );



    const selectedVideo =
        activeCurriculumVideo?.videoUrl ||
        "";

    const approvedCount =
        useMemo(
            () =>
                courses.filter(
                    (course) =>
                        isApprovedCourse(
                            course
                        )
                ).length,
            [
                courses,
                isApprovedCourse,
            ]
        );

    const pendingCount =
        useMemo(
            () =>
                courses.filter(
                    (course) =>
                        isPendingCourse(
                            course
                        )
                ).length,
            [
                courses,
                isPendingCourse,
            ]
        );

    const overallSidebarStatus =
        useMemo(() => {
            if (!courses.length) {
                return null;
            }

            if (pendingCount > 0) {
                return {
                    label:
                        approvedCount >
                        0
                            ? "Some Pending"
                            : "Pending / Verifying",
                    className:
                        "pending",
                };
            }

            return {
                label: "Approved",
                className:
                    "approved",
            };
        }, [
            courses,
            pendingCount,
            approvedCount,
        ]);

    const selectedCourseSections =
        selectedCourse
            ? courseSections[
                  selectedCourse.id
              ] || []
            : [];

    const selectedCourseSectionLoading =
        selectedCourse
            ? Boolean(
                  sectionsLoading[
                      selectedCourse.id
                  ]
              )
            : false;

    const selectedCourseSectionError =
        selectedCourse
            ? sectionsErrors[
                  selectedCourse.id
              ]
            : "";

    const totalCurriculumVideos =
        currentCourseCurriculum.length;

    const completedCount =
        selectedCourse
            ? currentCourseCurriculum.filter(
                  (video) =>
                      selectedCourseCompleted.has(
                          video.key
                      )
              ).length
            : 0;

    const courseProgressPercentage =
        totalCurriculumVideos > 0
            ? Math.round(
                  (completedCount /
                      totalCurriculumVideos) *
                      100
              )
            : 0;

    const totalDurationSeconds =
        currentCourseCurriculum.reduce(
            (sum, video) =>
                sum +
                Number(
                    video.durationSeconds ||
                        0
                ),
            0
        );

    if (
        academicYearLoading ||
        !academicYearInitialized
    ) {
        return (
            <>
                <style>{`
                    .cayep-learning-loading-page {
                        min-height: 70vh;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        background: #F4F7F9;
                        color: #142536;
                    }

                    .cayep-learning-spinner {
                        width: 44px;
                        height: 44px;
                        border: 4px solid #DCE5EC;
                        border-top-color: #062A4B;
                        border-right-color: #F6C944;
                        border-radius: 50%;
                        animation: cayepLearningSpin .8s linear infinite;
                        margin-bottom: 15px;
                    }

                    @keyframes cayepLearningSpin {
                        to {
                            transform: rotate(360deg);
                        }
                    }
                `}</style>

                <div className="cayep-learning-loading-page">
                    <div className="cayep-learning-spinner" />

                    <strong>
                        Preparing your learning environment...
                    </strong>
                </div>
            </>
        );
    }

    if (!selectedYear) {
        return (
            <>
                <style>{`
                    .cayep-learning-no-year {
                        min-height: 70vh;
                        display: grid;
                        place-items: center;
                        padding: 20px;
                        background: #F4F7F9;
                    }

                    .cayep-learning-no-year-card {
                        width: min(560px, 100%);
                        background: #FFFFFF;
                        border: 1px solid #E0E7EC;
                        border-radius: 24px;
                        padding: 42px 28px;
                        text-align: center;
                        box-shadow: 0 18px 50px rgba(6,42,75,.08);
                    }

                    .cayep-learning-no-year-card svg {
                        color: #C0750D;
                        font-size: 42px;
                        margin-bottom: 15px;
                    }

                    .cayep-learning-no-year-card h2 {
                        margin: 0 0 9px;
                        color: #062A4B;
                    }

                    .cayep-learning-no-year-card p {
                        margin: 0;
                        color: #6B7280;
                        line-height: 1.7;
                    }
                `}</style>

                <div className="cayep-learning-no-year">
                    <div className="cayep-learning-no-year-card">
                        <FaGraduationCap />

                        <h2>
                            Academic Year Unavailable
                        </h2>

                        <p>
                            There is currently no active
                            academic or training year available.
                        </p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
      

            <style>{`
    /* =========================================================
       CAYEP LEARNING
       Base Variables & Root
       ========================================================= */

    .cayep-learning {
        --navy: #062A4B;
        --deep-blue: #063A6B;
        --gold: #F6C944;
        --orange: #C0750D;
        --sky: #0F6DAB;
        --green: #4FAE22;
        --page: #F4F7F9;
        --text: #142536;
        --muted: #6B7280;
        --border: #E0E7EC;

        width: 100%;
        min-height: 100%;
        background: var(--page);
        color: var(--text);
        position: relative;
        z-index: 0;
        font-size: 15px;
    }

    .cayep-learning *,
    .cayep-learning *::before,
    .cayep-learning *::after {
        box-sizing: border-box;
    }


    /* =========================================================
       TOP BAR
       ========================================================= */

    .cayep-learning-topbar {
        width: 100%;
        min-height: 72px;
        padding: 0 clamp(14px, 3vw, 28px);
        background: #FFFFFF;
        border-bottom: 1px solid var(--border);
        display: flex;
        align-items: center;
        gap: 18px;
        position: relative;
        z-index: 50;
        box-shadow: 0 2px 10px rgba(6,42,75,.06);
    }

    .cayep-mobile-menu {
        display: none;
        width: 42px;
        height: 42px;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: #FFFFFF;
        color: var(--navy);
        cursor: pointer;
    }


    /* =========================================================
       BRAND
       ========================================================= */

    .cayep-learning-brand {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .cayep-learning-brand > div {
        width: 43px;
        height: 43px;
        display: grid;
        place-items: center;
        border-radius: 12px;
        background: var(--navy);
        color: var(--gold);
        font-size: 21px;
    }

    .cayep-learning-brand section {
        display: flex;
        flex-direction: column;
    }

    .cayep-learning-brand strong {
        color: var(--navy);
        font-size: 16px;
        line-height: 1.2;
        letter-spacing: .06em;
    }

    .cayep-learning-brand span {
        color: var(--orange);
        font-size: 10px;
        line-height: 1.3;
        font-weight: 900;
        letter-spacing: .13em;
    }


    /* =========================================================
       SEARCH
       ========================================================= */

    .cayep-learning-search {
        width: min(500px, 100%);
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 9px;
        min-height: 45px;
        padding: 0 13px;
        border: 1px solid var(--border);
        border-radius: 11px;
        background: #F8FAFB;
    }

    .cayep-learning-search svg {
        color: var(--sky);
        flex: 0 0 auto;
        font-size: 16px;
    }

    .cayep-learning-search input {
        width: 100%;
        min-width: 0;
        border: 0;
        outline: 0;
        background: transparent;
        color: var(--text);
        font: inherit;
        font-size: 14px;
    }


    /* =========================================================
       MAIN LAYOUT
       ========================================================= */

    .cayep-learning-layout {
        width: min(1600px, 100%);
        min-height: calc(100vh - 72px);
        margin: auto;
        display: grid;
        grid-template-columns: 350px minmax(0, 1fr);
        position: relative;
        z-index: 0;
    }


    /* =========================================================
       SIDEBAR
       ========================================================= */

    .cayep-learning-sidebar {
        min-width: 0;
        background: #FFFFFF;
        border-right: 1px solid var(--border);
        display: flex;
        flex-direction: column;
        position: relative;
        z-index: 20;
    }

    .cayep-sidebar-title {
        padding: 22px 18px 17px;
        border-bottom: 1px solid var(--border);
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
    }

    .cayep-sidebar-title span {
        color: var(--orange);
        font-size: 12px;
        font-weight: 900;
        letter-spacing: .13em;
    }

    .cayep-sidebar-title h2 {
        margin: 5px 0 0;
        color: var(--navy);
        font-size: 24px;
        line-height: 1.25;
    }

    .cayep-sidebar-summary {
        margin-top: 11px;
        display: flex;
        align-items: center;
        gap: 7px;
        flex-wrap: wrap;
    }

    .cayep-sidebar-summary-count {
        color: var(--muted);
        font-size: 12px;
        font-weight: 800;
    }

    .cayep-sidebar-status {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        width: fit-content;
        padding: 6px 10px;
        border-radius: 999px;
        font-size: 11px;
        line-height: 1;
        font-weight: 900;
        border: 1px solid transparent;
    }

    .cayep-sidebar-status.approved {
        color: #287719;
        background: #F0F9EC;
        border-color: #CBE8C3;
    }

    .cayep-sidebar-status.pending {
        color: #9A5D00;
        background: #FFF8E7;
        border-color: #F2DEAA;
    }

    .cayep-sidebar-close {
        display: none;
        width: 36px;
        height: 36px;
        border: 0;
        border-radius: 9px;
        background: #F2F5F7;
        color: var(--navy);
        cursor: pointer;
    }


    /* =========================================================
       COURSE LIST
       ========================================================= */

    .cayep-learning-course-list {
        flex: 1;
        overflow-y: auto;
        padding: 10px;
    }

    .cayep-course-tree {
        width: 100%;
        margin-bottom: 9px;
        border: 1px solid #E4EAF0;
        border-radius: 14px;
        overflow: hidden;
        background: #FFFFFF;
    }

    .cayep-course-tree.active {
        border-color: #C9DCE9;
        box-shadow: 0 7px 20px rgba(6,42,75,.05);
    }

    .cayep-course-tree.pending {
        background: #FFFCF5;
    }

    .cayep-course-main {
        width: 100%;
        border: 0;
        background: transparent;
        padding: 11px;
        display: grid;
        grid-template-columns: 62px minmax(0, 1fr) 28px;
        align-items: center;
        gap: 10px;
        text-align: left;
        cursor: pointer;
        color: var(--text);
    }

    .cayep-course-main:hover {
        background: #F6F9FB;
    }

    .cayep-course-main.active {
        background: #EEF4F8;
    }

    .cayep-course-main.pending {
        background: #FFFCF5;
    }


    /* =========================================================
       COURSE THUMBNAIL
       ========================================================= */

    .cayep-course-thumb {
        position: relative;
        width: 62px;
        height: 54px;
        overflow: hidden;
        border-radius: 8px;
        background: #E7EEF3;
        display: grid;
        place-items: center;
        color: var(--navy);
    }

    .cayep-course-thumb img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    .cayep-course-thumb.pending {
        filter: saturate(.65);
    }

    .cayep-course-thumb .cayep-play-icon {
        position: absolute;
        right: 5px;
        bottom: 5px;
        color: #FFFFFF;
        filter: drop-shadow(0 2px 3px rgba(0,0,0,.4));
    }

    .cayep-course-lock {
        position: absolute;
        top: 5px;
        left: 5px;
        width: 19px;
        height: 19px;
        display: grid;
        place-items: center;
        border-radius: 50%;
        background: #FFFFFF;
        color: var(--orange);
        font-size: 10px;
        box-shadow: 0 2px 6px rgba(0,0,0,.18);
    }


    /* =========================================================
       COURSE INFORMATION
       ========================================================= */

    .cayep-course-main-info {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    .cayep-course-main-info strong {
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        color: var(--navy);
        font-size: 15px;
        line-height: 1.4;
    }

    .cayep-course-facilitator {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--muted);
        font-size: 12px;
        line-height: 1.4;
    }

    .cayep-course-meta {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
    }

    .cayep-course-status {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        width: fit-content;
        max-width: 100%;
        padding: 5px 8px;
        border-radius: 999px;
        font-size: 10px;
        font-weight: 900;
        line-height: 1;
        border: 1px solid transparent;
    }

    .cayep-course-status.approved {
        color: #287719;
        background: #F0F9EC;
        border-color: #CBE8C3;
    }

    .cayep-course-status.pending {
        color: #9A5D00;
        background: #FFF8E7;
        border-color: #F2DEAA;
    }

    .cayep-course-progress-mini {
        color: var(--muted);
        font-size: 11px;
        font-weight: 800;
    }


    /* =========================================================
       COURSE EXPAND BUTTON
       ========================================================= */

    .cayep-course-expand-button {
        width: 27px;
        height: 27px;
        border: 1px solid var(--border);
        border-radius: 7px;
        background: #FFFFFF;
        color: var(--navy);
        display: grid;
        place-items: center;
        cursor: pointer;
        font-size: 11px;
    }

    .cayep-course-expand-button:hover {
        background: #F2F6F8;
    }


    /* =========================================================
       CURRICULUM
       ========================================================= */

    .cayep-course-curriculum {
        border-top: 1px solid #E9EEF2;
        background: #FBFCFD;
        padding: 7px 8px 9px;
    }

    .cayep-curriculum-label {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 7px 8px 9px;
    }

    .cayep-curriculum-label span:first-child {
        color: var(--navy);
        font-size: 11px;
        font-weight: 900;
        letter-spacing: .08em;
        text-transform: uppercase;
    }

    .cayep-curriculum-label span:last-child {
        color: var(--muted);
        font-size: 10px;
        font-weight: 800;
    }


    /* =========================================================
       CURRICULUM VIDEOS
       ========================================================= */

    .cayep-curriculum-video {
        width: 100%;
        min-width: 0;
        border: 0;
        border-radius: 9px;
        background: transparent;
        padding: 9px 8px;
        display: grid;
        grid-template-columns: 30px minmax(0, 1fr) auto;
        align-items: center;
        gap: 8px;
        text-align: left;
        color: var(--text);
        cursor: pointer;
        transition: background .16s ease;
    }

    .cayep-curriculum-video:hover {
        background: #EEF4F8;
    }

    .cayep-curriculum-video.current {
        background: #E7F0F6;
    }

    .cayep-curriculum-video.locked {
        cursor: not-allowed;
        opacity: .65;
    }

    .cayep-curriculum-video.locked:hover {
        background: #FFF8EA;
    }

    .cayep-curriculum-icon {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        display: grid;
        place-items: center;
        background: #EAF0F4;
        color: var(--navy);
        font-size: 10px;
    }

    .cayep-curriculum-video.current .cayep-curriculum-icon {
        background: var(--navy);
        color: var(--gold);
    }

    .cayep-curriculum-video.completed .cayep-curriculum-icon {
        background: #EAF7E6;
        color: var(--green);
    }

    .cayep-curriculum-video.locked .cayep-curriculum-icon {
        background: #F3F1EE;
        color: #8B7D6B;
    }

    .cayep-curriculum-copy {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 3px;
    }

    .cayep-curriculum-copy strong {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--navy);
        font-size: 13px;
        line-height: 1.45;
    }

    .cayep-curriculum-copy span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--muted);
        font-size: 11px;
        line-height: 1.4;
    }

    .cayep-curriculum-state {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        color: var(--muted);
        font-size: 10px;
        font-weight: 900;
        white-space: nowrap;
    }

    .cayep-curriculum-video.completed .cayep-curriculum-state {
        color: var(--green);
    }

    .cayep-curriculum-video.current .cayep-curriculum-state {
        color: var(--sky);
    }

    .cayep-curriculum-video.locked .cayep-curriculum-state {
        color: #9B8B76;
    }


    /* =========================================================
       CURRICULUM CONTROLS
       ========================================================= */

    .cayep-curriculum-expand {
        width: 100%;
        min-height: 36px;
        margin-top: 4px;
        border: 1px solid #DCE6EC;
        border-radius: 8px;
        background: #FFFFFF;
        color: var(--navy);
        font-size: 11px;
        font-weight: 900;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
    }

    .cayep-curriculum-expand:hover {
        background: #F1F6F9;
    }

    .cayep-curriculum-empty {
        padding: 11px 9px;
        color: var(--muted);
        font-size: 11px;
        line-height: 1.55;
    }

    .cayep-curriculum-error {
        margin: 5px;
        padding: 10px;
        border-radius: 8px;
        background: #FFF7F7;
        border: 1px solid #ECD7D7;
        color: #9B3A32;
        font-size: 11px;
        line-height: 1.55;
    }


    /* =========================================================
       PAGINATION
       ========================================================= */

    .cayep-learning-pagination {
        min-height: 60px;
        padding: 9px 12px;
        border-top: 1px solid var(--border);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        background: #FFFFFF;
    }

    .cayep-learning-pagination button {
        width: 36px;
        height: 36px;
        border: 1px solid var(--border);
        border-radius: 9px;
        background: #FFFFFF;
        color: var(--navy);
        cursor: pointer;
    }

    .cayep-learning-pagination button:disabled {
        opacity: .35;
        cursor: not-allowed;
    }

    .cayep-learning-pagination span {
        color: var(--muted);
        font-size: 12px;
        font-weight: 800;
    }


    /* =========================================================
       LOADING / EMPTY STATES
       ========================================================= */

    .cayep-learning-loading {
        padding: 30px 20px;
        color: var(--muted);
        text-align: center;
        font-size: 14px;
    }

    .cayep-no-learning {
        flex: 1;
        padding: 40px 22px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        color: var(--muted);
    }

    .cayep-no-learning svg {
        font-size: 40px;
        color: var(--navy);
        margin-bottom: 12px;
    }

    .cayep-no-learning h3 {
        margin: 0 0 8px;
        color: var(--navy);
        font-size: 19px;
        line-height: 1.3;
    }

    .cayep-no-learning p {
        margin: 0;
        font-size: 14px;
        line-height: 1.65;
    }


    /* =========================================================
       LEARNING WORKSPACE
       ========================================================= */

    .cayep-learning-workspace {
        min-width: 0;
        padding: clamp(14px, 2vw, 25px);
        position: relative;
        z-index: 0;
    }


    /* =========================================================
       WELCOME STATE
       ========================================================= */

    .cayep-learning-welcome {
        min-height: 70vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 30px;
    }

    .cayep-learning-welcome svg {
        color: var(--gold);
        background: var(--navy);
        width: 72px;
        height: 72px;
        padding: 18px;
        border-radius: 22px;
        margin-bottom: 18px;
    }

    .cayep-learning-welcome h1 {
        max-width: 650px;
        margin: 0 0 10px;
        color: var(--navy);
        font-size: clamp(27px, 4vw, 40px);
        line-height: 1.2;
    }

    .cayep-learning-welcome p {
        margin: 0;
        color: var(--muted);
        font-size: 16px;
        line-height: 1.65;
    }


    /* =========================================================
       VIDEO SECTION
       ========================================================= */

    .cayep-video-section {
        width: 100%;
    }

    .cayep-learning-video-player {
        width: 100%;
    }

    .cayep-video-wrapper {
        width: 100%;
        aspect-ratio: 16 / 9;
        max-height: 70vh;
        overflow: hidden;
        border-radius: 18px;
        background: #09151E;
        box-shadow: 0 16px 42px rgba(6,42,75,.12);
        position: relative;
    }

    .cayep-video-wrapper video {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: contain;
        background: #09151E;
    }


    /* =========================================================
       VIDEO EMPTY STATE
       ========================================================= */

    .cayep-no-video {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 30px;
        color: #FFFFFF;
    }

    .cayep-no-video svg {
        color: var(--gold);
        font-size: 48px;
        margin-bottom: 14px;
    }

    .cayep-no-video h3 {
        margin: 0 0 8px;
        font-size: 23px;
        line-height: 1.3;
    }

    .cayep-no-video p {
        max-width: 450px;
        margin: 0;
        color: rgba(255,255,255,.68);
        line-height: 1.65;
        font-size: 15px;
    }

    .cayep-video-error {
        position: absolute;
        left: 20px;
        right: 20px;
        bottom: 20px;
        padding: 12px 14px;
        border-radius: 10px;
        background: rgba(95, 20, 20, .92);
        color: #FFFFFF;
        font-size: 13px;
        line-height: 1.55;
    }


    /* =========================================================
       VIDEO HEADER
       ========================================================= */

    .cayep-video-top-label {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin: 0 0 10px;
        flex-wrap: wrap;
    }

    .cayep-video-top-label > div:first-child {
        min-width: 0;
    }

    .cayep-video-eyebrow {
        display: block;
        color: var(--orange);
        font-size: 11px;
        font-weight: 900;
        letter-spacing: .13em;
        margin-bottom: 5px;
    }

    .cayep-video-title {
        margin: 0;
        color: var(--navy);
        font-size: clamp(20px, 2.5vw, 29px);
        line-height: 1.3;
    }

    .cayep-video-counter {
        flex: 0 0 auto;
        padding: 8px 11px;
        border-radius: 999px;
        background: #EEF4F8;
        border: 1px solid #D8E5EC;
        color: var(--navy);
        font-size: 10px;
        font-weight: 900;
        white-space: nowrap;
    }

    .cayep-video-description {
        margin: 10px 0 0;
        color: var(--muted);
        font-size: 14px;
        line-height: 1.7;
    }


    /* =========================================================
       CURRENT COURSE
       ========================================================= */

    .cayep-current-course {
        margin: 17px 0 22px;
        padding: 20px;
        border: 1px solid var(--border);
        border-radius: 16px;
        background: #FFFFFF;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 20px;
    }

    .cayep-current-course > div {
        min-width: 0;
    }

    .cayep-current-course > div:first-child {
        flex: 1;
    }

    .cayep-current-course-header {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    .cayep-current-course-header > span:first-child {
        color: var(--orange);
        font-size: 11px;
        font-weight: 900;
        letter-spacing: .13em;
    }

    .cayep-current-course h1 {
        margin: 6px 0 8px;
        color: var(--navy);
        font-size: clamp(22px, 3vw, 31px);
        line-height: 1.25;
    }

    .cayep-current-course p {
        max-width: 850px;
        margin: 0;
        color: var(--muted);
        line-height: 1.7;
        font-size: 15px;
    }


    /* =========================================================
       COURSE INSTRUCTOR
       ========================================================= */

    .cayep-course-instructor {
        margin-top: 13px;
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--muted);
        font-size: 13px;
    }

    .cayep-course-instructor svg {
        color: var(--sky);
    }

    .cayep-course-instructor strong {
        color: var(--navy);
    }


    /* =========================================================
       COURSE APPROVAL / PENDING
       ========================================================= */

    .cayep-current-approval {
        margin-top: 11px;
        display: flex;
        align-items: center;
        gap: 6px;
        color: #287719;
        font-size: 12px;
        font-weight: 700;
    }

    .cayep-current-approval svg {
        flex: 0 0 auto;
    }

    .cayep-current-pending {
        margin-top: 12px;
        padding: 11px 13px;
        border: 1px solid #F0DDAE;
        border-radius: 10px;
        background: #FFF9EA;
        color: #8A5A00;
        font-size: 13px;
        line-height: 1.55;
    }



    /* =========================================================
       LEARNING PROGRESS
       ========================================================= */

    .cayep-learning-progress-card {
        margin: 0 0 18px;
        padding: 15px 17px;
        border: 1px solid var(--border);
        border-radius: 14px;
        background: #FFFFFF;
    }

    .cayep-learning-progress-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 9px;
    }

    .cayep-learning-progress-header strong {
        color: var(--navy);
        font-size: 13px;
    }

    .cayep-learning-progress-header span {
        color: var(--muted);
        font-size: 11px;
        font-weight: 800;
    }

    .cayep-progress-track {
        width: 100%;
        height: 8px;
        border-radius: 999px;
        background: #E9EFF3;
        overflow: hidden;
    }

    .cayep-progress-fill {
        height: 100%;
        border-radius: inherit;
        background: var(--green);
        transition: width .3s ease;
    }

    .cayep-progress-meta {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-top: 8px;
        color: var(--muted);
        font-size: 10px;
        font-weight: 700;
    }


    /* =========================================================
       CLASSROOM
       ========================================================= */

    .cayep-classroom-wrapper {
        width: 100%;
        min-width: 0;
        margin-top: 5px;
    }


    /* =========================================================
       AUTO ADVANCE
       ========================================================= */

    .cayep-auto-advance {
        margin: 12px 0;
        padding: 11px 13px;
        border: 1px solid #D9E8D3;
        border-radius: 10px;
        background: #F5FAF3;
        color: #397A2B;
        font-size: 12px;
        font-weight: 800;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .cayep-auto-advance svg {
        animation: cayepAutoSpin .8s linear infinite;
    }

    @keyframes cayepAutoSpin {
        to {
            transform: rotate(360deg);
        }
    }


    /* =========================================================
       ERROR STATE
       ========================================================= */

    .cayep-learning-error {
        width: min(700px, calc(100% - 30px));
        margin: 80px auto;
        padding: 30px;
        border: 1px solid #E8D5D5;
        border-radius: 18px;
        background: #FFFFFF;
        text-align: center;
    }

    .cayep-learning-error > svg {
        color: var(--orange);
        font-size: 40px;
        margin-bottom: 12px;
    }

    .cayep-learning-error h2 {
        color: var(--navy);
        margin: 0 0 8px;
        font-size: 24px;
    }

    .cayep-learning-error p {
        margin: 0;
        color: var(--muted);
        line-height: 1.7;
        font-size: 15px;
    }


    /* =========================================================
       PENDING APPROVAL ALERT
       ========================================================= */

    .cayep-pending-alert {
        padding: 2px 4px 6px;
        text-align: center;
    }

    .cayep-pending-alert-icon {
        width: 60px;
        height: 60px;
        margin: 0 auto 12px;
        display: grid;
        place-items: center;
        border-radius: 50%;
        background: #FFF7E5;
        color: #C0750D;
        border: 1px solid #F1D99D;
        font-size: 28px;
    }

    .cayep-pending-alert-title {
        margin-bottom: 9px;
        color: #062A4B;
        font-weight: 900;
        font-size: 18px;
    }

    .cayep-pending-alert p {
        margin: 7px auto;
        max-width: 500px;
        color: #5E6A74;
        line-height: 1.7;
        font-size: 14px;
    }

    .cayep-pending-alert strong {
        color: #062A4B;
    }


    /* =========================================================
       PENDING CONTACT
       ========================================================= */

    .cayep-pending-contact {
        max-width: 460px;
        margin: 15px auto 10px;
        padding: 13px;
        border: 1px solid #E0E7EC;
        border-radius: 11px;
        background: #F8FAFB;
        text-align: left;
    }

    .cayep-pending-contact > div {
        display: flex;
        align-items: center;
        gap: 7px;
        margin: 8px 0;
        color: #53616C;
        font-size: 13px;
        line-height: 1.5;
        word-break: break-word;
    }

    .cayep-pending-contact strong {
        color: #062A4B;
    }

    .cayep-contact-icon {
        width: 23px;
        flex: 0 0 23px;
        text-align: center;
        color: #0F6DAB;
        font-size: 15px;
    }

    .cayep-pending-note {
        margin: 12px auto 0;
        max-width: 480px;
        color: #7B8791;
        font-size: 11px;
        line-height: 1.6;
    }


    /* =========================================================
       RESPONSIVE
       Desktop → Tablet
       ========================================================= */

    @media (max-width: 1100px) {
        .cayep-learning-layout {
            grid-template-columns: 320px minmax(0, 1fr);
        }

        .cayep-course-main {
            grid-template-columns: 57px minmax(0, 1fr) 27px;
        }

        .cayep-course-thumb {
            width: 57px;
            height: 51px;
        }
    }


    /* =========================================================
       RESPONSIVE
       Tablet → Mobile
       ========================================================= */

    @media (max-width: 800px) {
        .cayep-mobile-menu {
            display: grid;
            place-items: center;
        }

        .cayep-learning-layout {
            display: block;
            min-height: calc(100vh - 72px);
        }

        .cayep-learning-sidebar {
            position: fixed;
            z-index: 1100;
            top: 0;
            bottom: 0;
            left: 0;
            width: min(390px, 92vw);
            transform: translateX(-105%);
            transition: transform .24s ease;
            box-shadow: 15px 0 45px rgba(0,0,0,.14);
        }

        .cayep-learning-sidebar.open {
            transform: translateX(0);
        }

        .cayep-sidebar-close {
            display: grid;
            place-items: center;
        }

        .cayep-learning-search {
            margin-left: auto;
        }

        .cayep-current-course {
            flex-direction: column;
        }

        .cayep-drop-course {
            width: 100%;
            justify-content: center;
        }

        /* Slightly larger mobile sidebar typography */
        .cayep-course-main-info strong {
            font-size: 15px;
        }

        .cayep-course-facilitator {
            font-size: 12px;
        }

        .cayep-course-progress-mini {
            font-size: 11px;
        }

        .cayep-course-status {
            font-size: 10px;
        }

        .cayep-curriculum-copy strong {
            font-size: 13px;
        }

        .cayep-curriculum-copy span {
            font-size: 11px;
        }

        .cayep-curriculum-state {
            font-size: 10px;
        }
    }


    /* =========================================================
       RESPONSIVE
       Small Mobile
       ========================================================= */

    @media (max-width: 600px) {
        .cayep-learning-topbar {
            min-height: 64px;
            gap: 8px;
            padding: 0 10px;
        }

        .cayep-mobile-menu {
            width: 40px;
            height: 40px;
        }

        .cayep-learning-brand > div {
            width: 40px;
            height: 40px;
            font-size: 18px;
        }

        .cayep-learning-brand strong {
            font-size: 14px;
        }

        .cayep-learning-brand span {
            font-size: 8px;
        }

        .cayep-learning-search {
            min-height: 40px;
            padding: 0 9px;
        }

        .cayep-learning-search input {
            font-size: 13px;
        }

        .cayep-learning-workspace {
            padding: 10px;
        }

        .cayep-video-wrapper {
            border-radius: 12px;
        }

        .cayep-current-course {
            padding: 16px;
            border-radius: 13px;
            margin-top: 11px;
        }

        .cayep-current-course h1 {
            font-size: 22px;
        }

        .cayep-current-course p {
            font-size: 15px;
            line-height: 1.7;
        }

        .cayep-course-instructor {
            font-size: 13px;
        }

        .cayep-classroom-wrapper {
            margin-top: 10px;
        }

        .cayep-video-title {
            font-size: 21px;
        }

        .cayep-video-description {
            font-size: 14px;
        }

        .cayep-learning-progress-card {
            padding: 14px;
        }

        .cayep-learning-progress-header strong {
            font-size: 13px;
        }

        .cayep-learning-progress-header span {
            font-size: 11px;
        }

        .cayep-progress-meta {
            font-size: 10px;
        }

        .cayep-pending-contact > div {
            align-items: flex-start;
            font-size: 13px;
        }

        .cayep-pending-note {
            font-size: 11px;
        }

        /* Keep sidebar readable on small screens */
        .cayep-course-main-info strong {
            font-size: 15px;
            line-height: 1.45;
        }

        .cayep-course-facilitator {
            font-size: 12px;
        }

        .cayep-course-progress-mini {
            font-size: 11px;
        }

        .cayep-course-status {
            font-size: 10px;
        }

        .cayep-curriculum-label span:first-child {
            font-size: 11px;
        }

        .cayep-curriculum-label span:last-child {
            font-size: 10px;
        }

        .cayep-curriculum-copy strong {
            font-size: 13px;
        }

        .cayep-curriculum-copy span {
            font-size: 11px;
        }

        .cayep-curriculum-state {
            font-size: 10px;
        }

        .cayep-curriculum-expand {
            font-size: 11px;
        }

        .cayep-curriculum-empty,
        .cayep-curriculum-error {
            font-size: 11px;
        }
    }


    /* =========================================================
       RESPONSIVE
       Extra Small Mobile
       ========================================================= */

    @media (max-width: 430px) {
        .cayep-learning-brand section {
            display: none;
        }

        .cayep-learning-search {
            width: auto;
            flex: 1;
        }

        .cayep-learning-search input {
            min-width: 0;
            font-size: 13px;
        }

        .cayep-learning-workspace {
            padding: 9px;
        }

        .cayep-course-main {
            grid-template-columns: 58px minmax(0, 1fr) 27px;
            gap: 9px;
            padding: 10px;
        }

        .cayep-course-thumb {
            width: 58px;
            height: 52px;
        }

        .cayep-course-main-info strong {
            font-size: 14px;
            line-height: 1.45;
        }

        .cayep-course-facilitator {
            font-size: 11px;
        }

        .cayep-course-progress-mini {
            font-size: 10px;
        }

        .cayep-course-status {
            font-size: 10px;
        }

        .cayep-curriculum-copy strong {
            font-size: 12.5px;
        }

        .cayep-curriculum-copy span {
            font-size: 10.5px;
        }

        .cayep-curriculum-state {
            font-size: 10px;
        }

        .cayep-current-course-header {
            align-items: flex-start;
            flex-direction: column;
            gap: 5px;
        }

        .cayep-current-course-header > span:first-child {
            font-size: 10px;
        }

        .cayep-current-course h1 {
            font-size: 22px;
        }

        .cayep-current-course p {
            font-size: 15px;
        }

        .cayep-video-eyebrow {
            font-size: 10px;
        }

        .cayep-video-title {
            font-size: 21px;
        }

        .cayep-video-counter {
            width: fit-content;
            font-size: 10px;
        }

        .cayep-video-description {
            font-size: 14px;
            line-height: 1.7;
        }

        .cayep-no-video h3 {
            font-size: 21px;
        }

        .cayep-no-video p {
            font-size: 14px;
        }

        .cayep-learning-welcome {
            padding: 22px 15px;
        }

        .cayep-learning-welcome h1 {
            font-size: 28px;
        }

        .cayep-learning-welcome p {
            font-size: 15px;
        }

        .cayep-pending-alert-title {
            font-size: 18px;
        }

        .cayep-pending-alert p {
            font-size: 14px;
        }
    }
`}</style>

            <div className="cayep-learning">
                <header className="cayep-learning-topbar">
                    <button
                        type="button"
                        className="cayep-mobile-menu"
                        onClick={() =>
                            setSidebarOpen(
                                (value) =>
                                    !value
                            )
                        }
                        aria-label="Open courses"
                    >
                        <FaBars />
                    </button>

                    <div className="cayep-learning-brand">
                        <div>
                            <FaGraduationCap />
                        </div>

                        <section>
                            <strong>
                                CAYEP
                            </strong>

                            <span>
                                MY LEARNING
                            </span>
                        </section>
                    </div>

                    <div className="cayep-learning-search">
                        <FaSearch />

                        <input
                            value={search}
                            onChange={(
                                event
                            ) => {
                                setSearch(
                                    event
                                        .target
                                        .value
                                );
                                setPage(
                                    1
                                );
                            }}
                            placeholder="Search my courses..."
                        />
                    </div>
                </header>

                {errorMessage ? (
                    <div className="cayep-learning-error">
                        <FaGraduationCap />

                        <h2>
                            Learning Environment Unavailable
                        </h2>

                        <p>
                            {errorMessage}
                        </p>
                    </div>
                ) : (
                    <main className="cayep-learning-layout">
                        <aside
                            className={
                                sidebarOpen
                                    ? "cayep-learning-sidebar open"
                                    : "cayep-learning-sidebar"
                            }
                        >
                            <div className="cayep-sidebar-title">
                                <div>
                                    <span>
                                        YOUR TRAINING
                                    </span>

                                    <h2>
                                        My Courses
                                    </h2>

                                    {overallSidebarStatus && (
                                        <div className="cayep-sidebar-summary">
                                            <span
                                                className={`cayep-sidebar-status ${overallSidebarStatus.className}`}
                                            >
                                                {overallSidebarStatus.className ===
                                                "approved" ? (
                                                    <FaCalendarCheck />
                                                ) : (
                                                    <FaClock />
                                                )}

                                                {
                                                    overallSidebarStatus.label
                                                }
                                            </span>

                                            <span className="cayep-sidebar-summary-count">
                                                {
                                                    courses.length
                                                }{" "}
                                                {courses.length ===
                                                1
                                                    ? "course"
                                                    : "courses"}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    className="cayep-sidebar-close"
                                    onClick={() =>
                                        setSidebarOpen(
                                            false
                                        )
                                    }
                                    aria-label="Close courses"
                                >
                                    <FaTimes />
                                </button>
                            </div>

                            {loading ? (
                                <div className="cayep-learning-loading">
                                    Loading your courses...
                                </div>
                            ) : courses.length ? (
                                <div className="cayep-learning-course-list">
                                    {courses.map(
                                        (
                                            course
                                        ) => {
                                            const approved =
                                                isApprovedCourse(
                                                    course
                                                );

                                            const statusClass =
                                                getStatusClass(
                                                    course
                                                );

                                            const approvalDate =
                                                formatApprovalDate(
                                                    course.approved_at
                                                );

                                            const curriculum =
                                                getCourseCurriculum(
                                                    course
                                                );

                                            const completed =
                                                getCourseCompletedSet(
                                                    course.id
                                                );

                                            const sectionVideos =
                                                curriculum.slice(
                                                    1
                                                );

                                            const visibleSections =
                                                getCourseVisibleSections(
                                                    course
                                                );

                                            const expanded =
                                                Boolean(
                                                    expandedCourses[
                                                        course.id
                                                    ]
                                                );

                                            const courseProgress =
                                                curriculum.length >
                                                0
                                                    ? Math.round(
                                                          (curriculum.filter(
                                                              (
                                                                  item
                                                              ) =>
                                                                  completed.has(
                                                                      item.key
                                                                  )
                                                          ).length /
                                                              curriculum.length) *
                                                              100
                                                      )
                                                    : 0;

                                            return (
                                                <div
                                                    key={
                                                        course.id
                                                    }
                                                    className={[
                                                        "cayep-course-tree",
                                                        statusClass,
                                                        selectedCourse?.id ===
                                                        course.id
                                                            ? "active"
                                                            : "",
                                                    ]
                                                        .filter(
                                                            Boolean
                                                        )
                                                        .join(
                                                            " "
                                                        )}
                                                >
                                                    <div
                                                        className={[
                                                            "cayep-course-main",
                                                            statusClass,
                                                            selectedCourse?.id ===
                                                            course.id
                                                                ? "active"
                                                                : "",
                                                        ]
                                                            .filter(
                                                                Boolean
                                                            )
                                                            .join(
                                                                " "
                                                            )}
                                                    >
                                                        <button
                                                            type="button"
                                                            style={{
                                                                all:
                                                                    "unset",
                                                                display:
                                                                    "contents",
                                                            }}
                                                            onClick={() =>
                                                                selectCourse(
                                                                    course
                                                                )
                                                            }
                                                        >
                                                            <div
                                                                className={[
                                                                    "cayep-course-thumb",
                                                                    statusClass,
                                                                ]
                                                                    .filter(
                                                                        Boolean
                                                                    )
                                                                    .join(
                                                                        " "
                                                                    )}
                                                            >
                                                                {getCourseThumbnailUrl(
                                                                    course
                                                                ) ? (
                                                                    <img
                                                                        src={getCourseThumbnailUrl(
                                                                            course
                                                                        )}
                                                                        alt=""
                                                                    />
                                                                ) : (
                                                                    <FaBookOpen />
                                                                )}

                                                                {!approved && (
                                                                    <span className="cayep-course-lock">
                                                                        <FaLock />
                                                                    </span>
                                                                )}

                                                                {approved && (
                                                                    <FaPlayCircle className="cayep-play-icon" />
                                                                )}
                                                            </div>

                                                            <section className="cayep-course-main-info">
                                                                <strong>
                                                                    {
                                                                        course.course_title
                                                                    }
                                                                </strong>

                                                                <span className="cayep-course-facilitator">
                                                                    {
                                                                        course.facilitator
                                                                    }
                                                                </span>

                                                                <div className="cayep-course-meta">
                                                                    <span
                                                                        className={`cayep-course-status ${statusClass}`}
                                                                    >
                                                                        {approved ? (
                                                                            <FaCalendarCheck />
                                                                        ) : (
                                                                            <FaClock />
                                                                        )}

                                                                        {getStatusLabel(
                                                                            course
                                                                        )}
                                                                    </span>

                                                                    {approved && (
                                                                        <span className="cayep-course-progress-mini">
                                                                            {
                                                                                courseProgress
                                                                            }
                                                                            %
                                                                            complete
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </section>
                                                        </button>

                                                        {approved &&
                                                            sectionVideos.length >
                                                                4 && (
                                                                <button
                                                                    type="button"
                                                                    className="cayep-course-expand-button"
                                                                    onClick={() =>
                                                                        toggleCourseExpansion(
                                                                            course.id
                                                                        )
                                                                    }
                                                                    aria-label={
                                                                        expanded
                                                                            ? "Collapse course videos"
                                                                            : "Expand course videos"
                                                                    }
                                                                    title={
                                                                        expanded
                                                                            ? "Show fewer videos"
                                                                            : "Show all videos"
                                                                    }
                                                                >
                                                                    {expanded ? (
                                                                        <FaChevronUp />
                                                                    ) : (
                                                                        <FaChevronDown />
                                                                    )}
                                                                </button>
                                                            )}
                                                    </div>

                                                    {approved && (
                                                        <div className="cayep-course-curriculum">
                                                            <div className="cayep-curriculum-label">
                                                                <span>
                                                                    Course Curriculum
                                                                </span>

                                                                <span>
                                                                    {
                                                                        curriculum.length
                                                                    }{" "}
                                                                    videos
                                                                </span>
                                                            </div>

                                                            {curriculum.length >
                                                                0 && (
                                                                <button
                                                                    type="button"
                                                                    className={[
                                                                        "cayep-curriculum-video",
                                                                        activeCurriculumVideo?.key ===
                                                                        curriculum[0]
                                                                            ?.key
                                                                            ? "current"
                                                                            : "",
                                                                        completed.has(
                                                                            curriculum[0]
                                                                                ?.key
                                                                        )
                                                                            ? "completed"
                                                                            : "",
                                                                    ]
                                                                        .filter(
                                                                            Boolean
                                                                        )
                                                                        .join(
                                                                            " "
                                                                        )}
                                                                    onClick={() =>
                                                                        selectCurriculumVideo(
                                                                            curriculum[0]
                                                                        )
                                                                    }
                                                                >
                                                                    <span className="cayep-curriculum-icon">
                                                                        {completed.has(
                                                                            curriculum[0]
                                                                                ?.key
                                                                        ) ? (
                                                                            <FaCheck />
                                                                        ) : activeCurriculumVideo?.key ===
                                                                          curriculum[0]
                                                                              ?.key ? (
                                                                            <FaPlay />
                                                                        ) : (
                                                                            <FaPlayCircle />
                                                                        )}
                                                                    </span>

                                                                    <span className="cayep-curriculum-copy">
                                                                        <strong>
                                                                            Video
                                                                            1
                                                                            —
                                                                            Course
                                                                            Introduction
                                                                        </strong>

                                                                        <span>
                                                                            {formatDuration(
                                                                                curriculum[0]
                                                                                    ?.durationSeconds
                                                                            ) ||
                                                                                "Introduction"}
                                                                        </span>
                                                                    </span>

                                                                    <span className="cayep-curriculum-state">
                                                                        {completed.has(
                                                                            curriculum[0]
                                                                                ?.key
                                                                        ) ? (
                                                                            <>
                                                                                <FaCheck />
                                                                                Done
                                                                            </>
                                                                        ) : activeCurriculumVideo?.key ===
                                                                          curriculum[0]
                                                                              ?.key ? (
                                                                            <>
                                                                                <FaPlay />
                                                                                Playing
                                                                            </>
                                                                        ) : (
                                                                            "Start"
                                                                        )}
                                                                    </span>
                                                                </button>
                                                            )}

                                                            {sectionsLoading[
                                                                course.id
                                                            ] ? (
                                                                <div className="cayep-curriculum-empty">
                                                                    Loading course
                                                                    videos...
                                                                </div>
                                                            ) : sectionsErrors[
                                                                  course.id
                                                              ] ? (
                                                                <div className="cayep-curriculum-error">
                                                                    {
                                                                        sectionsErrors[
                                                                            course
                                                                                .id
                                                                        ]
                                                                    }
                                                                </div>
                                                            ) : sectionVideos.length >
                                                              0 ? (
                                                                <>
                                                                    {visibleSections.map(
                                                                        (
                                                                            video
                                                                        ) => {
                                                                            const isCompleted =
                                                                                completed.has(
                                                                                    video.key
                                                                                );

                                                                            const isUnlocked =
                                                                                isCurriculumVideoUnlocked(
                                                                                    curriculum,
                                                                                    video.index,
                                                                                    completed
                                                                                );

                                                                            const isCurrent =
                                                                                activeCurriculumVideo?.key ===
                                                                                video.key;

                                                                            return (
                                                                                <button
                                                                                    key={
                                                                                        video.key
                                                                                    }
                                                                                    type="button"
                                                                                    className={[
                                                                                        "cayep-curriculum-video",
                                                                                        isCurrent
                                                                                            ? "current"
                                                                                            : "",
                                                                                        isCompleted
                                                                                            ? "completed"
                                                                                            : "",
                                                                                        !isUnlocked
                                                                                            ? "locked"
                                                                                            : "",
                                                                                    ]
                                                                                        .filter(
                                                                                            Boolean
                                                                                        )
                                                                                        .join(
                                                                                            " "
                                                                                        )}
                                                                                    onClick={() =>
                                                                                        selectCurriculumVideo(
                                                                                            video
                                                                                        )
                                                                                    }
                                                                                    aria-disabled={
                                                                                        !isUnlocked
                                                                                    }
                                                                                >
                                                                                    <span className="cayep-curriculum-icon">
                                                                                        {isCompleted ? (
                                                                                            <FaCheck />
                                                                                        ) : !isUnlocked ? (
                                                                                            <FaLock />
                                                                                        ) : isCurrent ? (
                                                                                            <FaPlay />
                                                                                        ) : (
                                                                                            <FaPlayCircle />
                                                                                        )}
                                                                                    </span>

                                                                                    <span className="cayep-curriculum-copy">
                                                                                        <strong>
                                                                                            Video{" "}
                                                                                            {
                                                                                                video.sectionNumber
                                                                                            }{" "}
                                                                                            —{" "}
                                                                                            {
                                                                                                video.title
                                                                                            }
                                                                                        </strong>

                                                                                        <span>
                                                                                            {formatDuration(
                                                                                                video.durationSeconds
                                                                                            ) ||
                                                                                                "Video lesson"}
                                                                                        </span>
                                                                                    </span>

                                                                                    <span className="cayep-curriculum-state">
                                                                                        {isCompleted ? (
                                                                                            <>
                                                                                                <FaCheck />
                                                                                                Done
                                                                                            </>
                                                                                        ) : !isUnlocked ? (
                                                                                            <>
                                                                                                <FaLock />
                                                                                                Locked
                                                                                            </>
                                                                                        ) : isCurrent ? (
                                                                                            <>
                                                                                                <FaPlay />
                                                                                                Playing
                                                                                            </>
                                                                                        ) : (
                                                                                            <>
                                                                                                <FaPlayCircle />
                                                                                                Open
                                                                                            </>
                                                                                        )}
                                                                                    </span>
                                                                                </button>
                                                                            );
                                                                        }
                                                                    )}

                                                                    {sectionVideos.length >
                                                                        INITIAL_VISIBLE_SECTION_COUNT && (
                                                                        <button
                                                                            type="button"
                                                                            className="cayep-curriculum-expand"
                                                                            onClick={() =>
                                                                                toggleCourseExpansion(
                                                                                    course.id
                                                                                )
                                                                            }
                                                                        >
                                                                            {expanded ? (
                                                                                <>
                                                                                    <FaChevronUp />
                                                                                    Show fewer
                                                                                    videos
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <FaChevronDown />
                                                                                    Show{" "}
                                                                                    {sectionVideos.length -
                                                                                        INITIAL_VISIBLE_SECTION_COUNT}{" "}
                                                                                    more
                                                                                    videos
                                                                                </>
                                                                            )}
                                                                        </button>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <div className="cayep-curriculum-empty">
                                                                    No additional
                                                                    video sections
                                                                    have been added
                                                                    to this course
                                                                    yet.
                                                                </div>
                                                            )}

                                                            {approvalDate && (
                                                                <div className="cayep-approved-date">
                                                                    <FaCalendarCheck />

                                                                    Approved:{" "}
                                                                    {
                                                                        approvalDate
                                                                    }
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        }
                                    )}
                                </div>
                            ) : (
                                <div className="cayep-no-learning">
                                    <FaBookOpen />

                                    <h3>
                                        No Courses Yet
                                    </h3>

                                    <p>
                                        Your submitted
                                        course plan will
                                        appear here after
                                        it has been processed.
                                    </p>
                                </div>
                            )}

                            {totalPages > 1 && (
                                <div className="cayep-learning-pagination">
                                    <button
                                        type="button"
                                        disabled={
                                            page <=
                                            1
                                        }
                                        onClick={() =>
                                            setPage(
                                                (
                                                    value
                                                ) =>
                                                    value -
                                                    1
                                            )
                                        }
                                        aria-label="Previous page"
                                    >
                                        <FaChevronLeft />
                                    </button>

                                    <span>
                                        {page} /{" "}
                                        {
                                            totalPages
                                        }
                                    </span>

                                    <button
                                        type="button"
                                        disabled={
                                            page >=
                                            totalPages
                                        }
                                        onClick={() =>
                                            setPage(
                                                (
                                                    value
                                                ) =>
                                                    value +
                                                    1
                                            )
                                        }
                                        aria-label="Next page"
                                    >
                                        <FaChevronRight />
                                    </button>
                                </div>
                            )}
                        </aside>

                        <section className="cayep-learning-workspace">
                            {!selectedCourse ? (
                                <div className="cayep-learning-welcome">
                                    <FaGraduationCap />

                                    <h1>
                                        Welcome to Your
                                        Learning Environment
                                    </h1>

                                    <p>
                                        Select an approved
                                        course from your
                                        training list to
                                        begin learning.
                                    </p>
                                </div>
                            ) : isPendingCourse(
                                  selectedCourse
                              ) ? (
                                <div className="cayep-learning-welcome">
                                    <FaLock />

                                    <h1>
                                        Course Awaiting
                                        Approval
                                    </h1>

                                    <p>
                                        Your payment for{" "}
                                        <strong>
                                            {
                                                selectedCourse.course_title
                                            }
                                        </strong>{" "}
                                        is currently
                                        pending verification.
                                    </p>

                                    <button
                                        type="button"
                                        className="cayep-drop-course"
                                        style={{
                                            marginTop: 20,
                                            width: "auto",
                                        }}
                                        onClick={() =>
                                            showPendingNotice(
                                                selectedCourse
                                            )
                                        }
                                    >
                                        <FaClock />

                                        View Approval
                                        Information
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <section className="cayep-video-section">
                                        <div className="cayep-video-top-label">
                                            <div>
                                                <span className="cayep-video-eyebrow">
                                                    NOW LEARNING
                                                </span>

                                                <h2 className="cayep-video-title">
                                                    {
                                                        activeCurriculumVideo?.title
                                                    }
                                                </h2>
                                            </div>

                                            <span className="cayep-video-counter">
                                                Video{" "}
                                                {activeCurriculumVideo
                                                    ? activeCurriculumVideo.index +
                                                      1
                                                    : 1}{" "}
                                                of{" "}
                                                {
                                                    totalCurriculumVideos
                                                }
                                            </span>
                                        </div>

                                        {activeCurriculumVideo?.description && (
                                            <p className="cayep-video-description">
                                                {
                                                    activeCurriculumVideo.description
                                                }
                                            </p>
                                        )}

                                        {pendingAutoAdvance && (
                                            <div className="cayep-auto-advance">
                                                <FaClock />

                                                Lesson completed.
                                                Preparing the
                                                next lesson...
                                            </div>
                                        )}

                                        <div className="cayep-video-wrapper cayep-learning-video-player">
                                            {selectedVideo ? (
                                                <video
                                                    ref={
                                                        videoRef
                                                    }
                                                    key={
                                                        activeCurriculumVideo?.key ||
                                                        selectedVideo
                                                    }
                                                    controls
                                                    playsInline
                                                    preload="metadata"
                                                    poster={
                                                        activeCurriculumVideo?.thumbnail ||
                                                        undefined
                                                    }
                                                    onLoadedData={() =>
                                                        setVideoLoading(
                                                            false
                                                        )
                                                    }
                                                    onCanPlay={() =>
                                                        setVideoLoading(
                                                            false
                                                        )
                                                    }
                                                    onWaiting={() =>
                                                        setVideoLoading(
                                                            true
                                                        )
                                                    }
                                                    onPlaying={() =>
                                                        setVideoLoading(
                                                            false
                                                        )
                                                    }
                                                    onEnded={
                                                        handleVideoCompleted
                                                    }
                                                    onError={() => {
                                                        setVideoLoading(
                                                            false
                                                        );

                                                        setVideoError(
                                                            "This video could not be played. Please try again or contact your training administrator."
                                                        );
                                                    }}
                                                >
                                                    <source
                                                        src={
                                                            selectedVideo
                                                        }
                                                    />

                                                    Your browser does not
                                                    support video playback.
                                                </video>
                                            ) : (
                                                <div className="cayep-no-video">
                                                    <FaPlayCircle />

                                                    <h3>
                                                        Video Coming Soon
                                                    </h3>

                                                    <p>
                                                        The facilitator
                                                        has not uploaded
                                                        the course
                                                        introduction video
                                                        yet.
                                                    </p>
                                                </div>
                                            )}

                                            {videoError && (
                                                <div className="cayep-video-error">
                                                    {
                                                        videoError
                                                    }
                                                </div>
                                            )}
                                        </div>

                                        <div className="cayep-current-course">
                                            <div>
                                                <div className="cayep-current-course-header">
                                                    <span>
                                                        CURRENT COURSE
                                                    </span>

                                                    <span className="cayep-course-status approved">
                                                        <FaCalendarCheck />

                                                        Approved
                                                    </span>
                                                </div>

                                                <h1>
                                                    {
                                                        selectedCourse.course_title
                                                    }
                                                </h1>

                                                <p>
                                                    {
                                                        selectedCourse.description
                                                    }
                                                </p>

                                                <div className="cayep-course-instructor">
                                                    <FaUserTie />

                                                    <span>
                                                        Facilitator:{" "}
                                                        <strong>
                                                            {
                                                                selectedCourse.facilitator
                                                            }
                                                        </strong>
                                                    </span>
                                                </div>

                                                {formatApprovalDate(
                                                    selectedCourse.approved_at
                                                ) && (
                                                    <div className="cayep-current-approval">
                                                        <FaCalendarCheck />

                                                        <span>
                                                            Course
                                                            approved on{" "}
                                                            <strong>
                                                                {formatApprovalDate(
                                                                    selectedCourse.approved_at
                                                                )}
                                                            </strong>
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                 
                                        </div>

                                        <div className="cayep-learning-progress-card">
                                            <div className="cayep-learning-progress-header">
                                                <strong>
                                                    Course Progress
                                                </strong>

                                                <span>
                                                    {
                                                        completedCount
                                                    }{" "}
                                                    of{" "}
                                                    {
                                                        totalCurriculumVideos
                                                    }{" "}
                                                    videos
                                                    completed
                                                </span>
                                            </div>

                                            <div className="cayep-progress-track">
                                                <div
                                                    className="cayep-progress-fill"
                                                    style={{
                                                        width: `${courseProgressPercentage}%`,
                                                    }}
                                                />
                                            </div>

                                            <div className="cayep-progress-meta">
                                                <span>
                                                    {
                                                        courseProgressPercentage
                                                    }
                                                    %
                                                    complete
                                                </span>

                                                <span>
                                                    {selectedCourseSectionLoading
                                                        ? "Loading curriculum..."
                                                        : `${selectedCourseSections.length} section ${
                                                              selectedCourseSections.length ===
                                                              1
                                                                  ? "video"
                                                                  : "videos"
                                                          }${
                                                              totalDurationSeconds >
                                                              0
                                                                  ? ` • ${formatDuration(
                                                                        totalDurationSeconds
                                                                    )}`
                                                                  : ""
                                                          }`}
                                                </span>
                                            </div>
                                        </div>
                                    </section>

                <section className="cayep-classroom-wrapper">
    <ClassRoomEnvironment
        course={selectedCourse}
        videoType={
            activeCurriculumVideo?.type ||
            "introduction"
        }
        videoSection={
            activeCurriculumVideo?.type ===
            "section"
                ? activeCurriculumVideo?.raw ||
                  selectedCourseSections.find(
                      (section) =>
                          String(section.id) ===
                          String(
                              activeCurriculumVideo.sectionId
                          )
                  ) ||
                  null
                : null
        }
    />
</section>
                                </>
                            )}
                        </section>
                    </main>
                )}
            </div>
        </>
    );
}

export default MyLearningCoursesEnvironment;
